import { isMobile } from '@/consts/device';

import { getAppStore, setAppStore } from '@/store';
import { librarySectionsQueryOptions } from '@/hooks/queries/useLibrarySectionsQuery';
import { queryClient } from '@/utils/query/queryClient';
import normalizeCategorySlug from '@/utils/ui/normalizeCategorySlug';
import selectPresetBySlug from '@/utils/preset/selectPresetBySlug';
import { Screen } from '@/types';

import type { Deeplink } from '../types';
import { DeeplinkKind } from '../types';

type CategoryDeeplink = Extract<Deeplink, { kind: DeeplinkKind.Category }>;

/**
 * Category deeplink: open the library at the named category and, on desktop,
 * load its first preset. Section order is `pinDeeplinkSections`' job.
 *
 * With `seeAll=true` the card-row home opens the category's **full grid**
 * instead — the same screen a row's own "See all" reaches. It is opt-in rather
 * than the default because a bare `genaicategory` link deliberately pins the
 * row at the top of the feed and keeps the rest of the library browsable; that
 * choice replaced an earlier version of this handler which did open the grid
 * (see `useSeeAllNavigation`), so flipping the default back would undo it.
 *
 * Writing the slug is enough: `useSeeAllNavigation` resolves it against the
 * loaded sections and `CategoryRowsHome` renders `SeeAllCategory` only on a hit,
 * so an unknown slug falls back to the pinned feed rather than a blank screen.
 * The header title and the browser back-step both follow from that same hook.
 */
const categoryHandler = async ({
  category,
  seeAll,
}: CategoryDeeplink): Promise<void> => {
  const categorySlug = normalizeCategorySlug(category);
  if (!categorySlug) return;

  const { activeTabSlug } = getAppStore();
  setAppStore({
    ...(activeTabSlug !== categorySlug && { activeTabSlug: categorySlug }),
    screen: Screen.Library,
    shouldScrollToActiveTab: true,
    // Card-row home only — the desktop layout never mounts `CategoryRowsHome`,
    // so the flag is inert there and the preset auto-select below still runs.
    ...(seeAll && { seeAllCategorySlug: categorySlug }),
  });

  if (isMobile) return;

  setAppStore({ hasAutoSelectedPreset: true, isPresetLoading: true });

  try {
    const sections = await queryClient.fetchQuery(librarySectionsQueryOptions);
    const firstSlug = sections?.find((section) => section.slug === categorySlug)
      ?.items[0]?.slug;

    if (!firstSlug) {
      setAppStore({ hasAutoSelectedPreset: false, isPresetLoading: false });
      return;
    }

    await selectPresetBySlug(firstSlug, queryClient);
  } catch {
    setAppStore({ hasAutoSelectedPreset: false, isPresetLoading: false });
  }
};

export default categoryHandler;
